import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { Contact } from "@/components/site/Contact";
import { Lightbox } from "@/components/site/Lightbox";
import { gallerySrc } from "@/data/galleryAssets";
import manifest from "@/data/galleryManifest.json";
import {
  CATEGORY_LABELS,
  CATEGORY_ORDER,
  categoryFor,
  type GalleryCategory,
} from "@/data/galleryCategories";

export const Route = createFileRoute("/gallery")({
  component: GalleryPage,
  head: () => ({
    meta: [
      { title: "Full Gallery — 30 Years of Custom Tile Work | RKD Tile" },
      {
        name: "description",
        content:
          "Browse the complete RKD Tile project archive — custom showers, backsplashes, fireplaces, mosaics, and natural stone installs across Utah.",
      },
      { property: "og:title", content: "Full Gallery — RKD Tile" },
      { property: "og:url", content: "https://rkdtile.com/gallery" },
      {
        property: "og:description",
        content:
          "Browse the complete RKD Tile project archive — 300+ documented custom tile and stone installs across Utah.",
      },
    ],
    links: [{ rel: "canonical", href: "https://rkdtile.com/gallery" }],
  }),
});

const FILES: string[] = manifest as string[];
const PHOTOS: string[] = FILES.map((f) => gallerySrc(f));

type TabKey = "all" | GalleryCategory;

const ALT_BY_CATEGORY: Record<GalleryCategory, string> = {
  backsplashes: "Custom tile backsplash installed by RKD Tile in a Utah kitchen",
  bathrooms: "Custom tile bathroom remodel completed by RKD Tile in Utah",
  floors: "Tile and natural stone floor installation by RKD Tile in Utah",
  kitchens: "Custom kitchen tile and stone work by RKD Tile in Utah",
  showers: "Custom tile shower with waterproofed walls built by RKD Tile in Utah",
  other: "Custom tile and natural stone craftsmanship by RKD Tile in Utah",
};

function describe(file: string, index: number) {
  return `${ALT_BY_CATEGORY[categoryFor(file)]} — project photo ${index + 1}`;
}

function GalleryPage() {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [tab, setTab] = useState<TabKey>("all");

  const counts = useMemo(() => {
    const c: Record<GalleryCategory, number> = {
      backsplashes: 0,
      bathrooms: 0,
      floors: 0,
      kitchens: 0,
      showers: 0,
      other: 0,
    };
    for (const f of FILES) c[categoryFor(f)]++;
    return c;
  }, []);

  const visible = useMemo(() => {
    const all = PHOTOS.map((src, i) => ({ src, i, f: FILES[i]! }));
    return tab === "all" ? all : all.filter((x) => categoryFor(x.f) === tab);
  }, [tab]);

  const lightboxImages = useMemo(() => visible.map((v) => v.src), [visible]);
  const openAt = (visibleIdx: number) => setLightboxIndex(visibleIdx);

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main>
        <section className="py-16 md:py-24 bg-cream border-b border-border">
          <div className="container-prose text-center max-w-3xl mx-auto">
            <span className="eyebrow">The full archive</span>
            <h1 className="mt-3 font-display text-4xl md:text-6xl text-foreground leading-tight">
              Complete Project Gallery
            </h1>
            <p className="mt-5 text-muted-foreground text-lg leading-relaxed">
              {PHOTOS.length} documented installs from 30 years of custom tile and stone work across Utah.
            </p>
            <Link to="/" className="inline-block mt-6 text-sm uppercase tracking-widest text-primary hover:underline">← Back to home</Link>
          </div>
        </section>

        <section className="py-10 md:py-14 bg-background">
          <div className="container-prose">
            <div className="mb-10 flex flex-wrap justify-center gap-2 md:gap-3">
              <button
                type="button"
                onClick={() => setTab("all")}
                className={`px-4 md:px-5 py-2.5 text-xs md:text-sm uppercase tracking-widest border transition-colors ${
                  tab === "all"
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-background text-foreground border-border hover:border-primary"
                }`}
              >
                All <span className="ml-1 opacity-70">({PHOTOS.length})</span>
              </button>
              {CATEGORY_ORDER.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setTab(c)}
                  className={`px-4 md:px-5 py-2.5 text-xs md:text-sm uppercase tracking-widest border transition-colors ${
                    tab === c
                      ? "bg-primary text-primary-foreground border-primary"
                      : "bg-background text-foreground border-border hover:border-primary"
                  }`}
                >
                  {CATEGORY_LABELS[c]}{" "}
                  <span className="ml-1 opacity-70">({counts[c]})</span>
                </button>
              ))}
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 md:gap-4">
              {visible.map((v, vi) => {
                const desc = describe(v.f, vi);
                return (
                <button
                  key={v.src}
                  type="button"
                  onClick={() => openAt(vi)}
                  aria-label={`Enlarge photo: ${desc}`}
                  className="group relative overflow-hidden bg-cream aspect-square focus:outline-none focus:ring-2 focus:ring-primary block w-full"
                >
                  <img
                    src={v.src}
                    alt={desc}
                    loading={vi < 4 ? "eager" : "lazy"}
                    fetchPriority={vi === 0 ? "high" : "auto"}
                    decoding="async"
                    width={600}
                    height={600}
                    onError={(e) => {
                      (e.currentTarget as HTMLImageElement).style.display = "none";
                    }}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </button>
                );
              })}
            </div>
            {visible.length === 0 && (
              <p className="text-center text-muted-foreground py-16">
                No photos in this category yet.
              </p>
            )}
          </div>
        </section>
        <Contact />
      </main>
      <Footer />

      {lightboxIndex !== null && (
        <Lightbox
          images={lightboxImages}
          index={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onIndexChange={setLightboxIndex}
        />
      )}
    </div>
  );
}